/**
 * Generic inventory-price scenario kernel (price-calibration velocity +
 * scenario plan, Task 1, 2026-08-26 —
 * docs/plans/2026-08-26-price-calibration-existing-inventory-design.md).
 *
 * Page-neutral pure math for "what would this SKU's remaining owned
 * inventory earn at price P, at the recently observed sales rate?" Nothing
 * here talks to the API, the schemas package, or React — this module is the
 * shared arithmetic that price-calibration.ts's simulator delegates to
 * (Task 1) and that the live scope/scenario UI will drive directly
 * (Tasks 2-3).
 *
 * Locked decisions this module enforces:
 *   - Cents are integers end to end.
 *   - A SKU's selected units come from an explicit scope (sellable-now vs
 *     all-owned); zero selected units contribute nothing to any financial
 *     or coverage total, at any price.
 *   - Missing cost/fee/velocity inputs are excluded and counted, never
 *     coerced to zero. A KNOWN zero (no recent sales; zero units selected)
 *     is a real number and must be kept distinct from an UNKNOWN one
 *     (insufficient/absent sales history), which stays null.
 *   - Scenario deltas are computed "at the same sales rate" as the recent
 *     observed velocity — never a demand forecast.
 */

// ─────────────────────────────────────────────────────────
// Inventory scope — sellable-now vs all-owned unit selection.
// ─────────────────────────────────────────────────────────

export type InventoryScope = 'sellable-now' | 'all-owned';

export interface InventoryBuckets {
  /** Amazon FBA fulfillable listing units only. */
  sellableNow: number;
  /** Fulfillable + reserved + inbound working/shipped/receiving +
   *  unambiguously-mapped warehouse-ready units. */
  allOwned: number;
}

export function selectedUnitsForScope(buckets: InventoryBuckets, scope: InventoryScope): number {
  return scope === 'sellable-now' ? buckets.sellableNow : buckets.allOwned;
}

// ─────────────────────────────────────────────────────────
// Shared low-level math — fees and unit profit. price-calibration.ts's
// feesAtPriceCents/simulateRowAtPrice delegate to these so the two callers
// can never drift.
// ─────────────────────────────────────────────────────────

export interface FeeBasis {
  referralPct: number | null;
  fixedFeeCents: number | null;
}

/** fees = round(referralPct × priceCents + fixedFeeCents). Null without a
 *  complete fee basis — never a silent zero. */
export function feesCents(basis: FeeBasis, priceCents: number): number | null {
  if (basis.referralPct == null || basis.fixedFeeCents == null) return null;
  return Math.round(basis.referralPct * priceCents + basis.fixedFeeCents);
}

/** unit profit = price − fees − landed unit cost. Null when fees or cost is
 *  unknown. `costCents` here is always the canonical landed Amazon
 *  listing-unit cost (multipack conversion applied exactly once upstream by
 *  the caller) — this function does no unit conversion itself. */
export function unitProfitCents(
  priceCents: number,
  feesCents: number | null,
  costCents: number | null
): number | null {
  if (feesCents == null || costCents == null) return null;
  return priceCents - feesCents - costCents;
}

// ─────────────────────────────────────────────────────────
// Row economics at one price.
// ─────────────────────────────────────────────────────────

export interface ScenarioRowInput {
  /** Seller SKU or other stable row identity — carried through for
   *  multi-SKU scenario maps/aggregation. */
  key: string;
  /** Exact units in the caller's chosen scope. Zero means "own none of this
   *  right now" and must not move any total. */
  selectedUnits: number;
  /** Canonical landed Amazon listing-unit cost. Null = no cost basis. */
  costCents: number | null;
  referralPct: number | null;
  fixedFeeCents: number | null;
  /** Recent non-cancelled Amazon sales, units/day. Null = unknown/
   *  insufficient sales history (never coerced to zero — replenishment is
   *  not evidence of price response, and absence of data is not "no
   *  demand"). Zero is a valid, KNOWN observation. */
  recentUnitsPerDay: number | null;
}

export interface RowPriceEconomics {
  priceCents: number;
  feesCents: number | null;
  unitProfitCents: number | null;
  /** selectedUnits × priceCents — needs only a price, not a cost basis. */
  remainingInventoryRevenueCents: number | null;
  /** selectedUnits × unit profit — "profit if these N selected units sell." */
  remainingInventoryProfitCents: number | null;
  /** min(selectedUnits, recentUnitsPerDay × 30). Null only when velocity is
   *  UNKNOWN (recentUnitsPerDay null); zero when velocity is known-zero or
   *  selectedUnits is zero. */
  thirtyDayUnits: number | null;
  /** thirtyDayUnits × unit profit, at the same recently observed sales
   *  rate. Null when either the unit profit or the velocity is unknown. */
  thirtyDayProfitCents: number | null;
}

export function computeRowPriceEconomics(
  input: ScenarioRowInput,
  priceCents: number
): RowPriceEconomics {
  const fees = feesCents(input, priceCents);
  const unitProfit = unitProfitCents(priceCents, fees, input.costCents);

  // `|| 0` normalizes a possible -0 (e.g. 0 selected units × a negative
  // unit profit) to +0 — a zero-stock row must read as an unambiguous zero.
  const remainingInventoryRevenueCents = input.selectedUnits * priceCents || 0;
  const remainingInventoryProfitCents =
    unitProfit == null ? null : input.selectedUnits * unitProfit || 0;

  const thirtyDayUnits =
    input.recentUnitsPerDay == null
      ? null
      : Math.min(input.selectedUnits, input.recentUnitsPerDay * 30) || 0;
  const thirtyDayProfitCents =
    unitProfit == null || thirtyDayUnits == null ? null : thirtyDayUnits * unitProfit || 0;

  return {
    priceCents,
    feesCents: fees,
    unitProfitCents: unitProfit,
    remainingInventoryRevenueCents,
    remainingInventoryProfitCents,
    thirtyDayUnits,
    thirtyDayProfitCents,
  };
}

// ─────────────────────────────────────────────────────────
// Current vs proposed — the live "try a price" scenario for one row.
// ─────────────────────────────────────────────────────────

export interface RowScenarioResult {
  key: string;
  selectedUnits: number;
  current: RowPriceEconomics;
  proposed: RowPriceEconomics;
  /** proposed remaining-inventory profit − current remaining-inventory
   *  profit, at the same sales rate. Null when either side has no cost
   *  basis (never zero — a missing number is not "no change"). */
  sellThroughProfitDeltaCents: number | null;
}

export function computeRowScenario(
  input: ScenarioRowInput,
  currentPriceCents: number,
  proposedPriceCents: number
): RowScenarioResult {
  const current = computeRowPriceEconomics(input, currentPriceCents);
  const proposed = computeRowPriceEconomics(input, proposedPriceCents);

  const sellThroughProfitDeltaCents =
    current.remainingInventoryProfitCents == null || proposed.remainingInventoryProfitCents == null
      ? null
      : proposed.remainingInventoryProfitCents - current.remainingInventoryProfitCents;

  return {
    key: input.key,
    selectedUnits: input.selectedUnits,
    current,
    proposed,
    sellThroughProfitDeltaCents,
  };
}

// ─────────────────────────────────────────────────────────
// Discount give-up — units needed to offset a lower price.
// ─────────────────────────────────────────────────────────

export interface DiscountOffsetResult {
  /** current unit profit − proposed unit profit. Positive = the proposed
   *  price gives up profit per unit (a discount); negative = it gains
   *  profit per unit (a raise). Null when either side has no cost basis. */
  giveUpPerUnitCents: number | null;
  /** Additional units, rounded up, that must sell at the proposed price to
   *  match the CURRENT 30-day (same-velocity) profit baseline. Null when
   *  not computable: no cost basis on either side, a nonpositive proposed
   *  unit profit (see `recoverable`), or an unknown current-velocity
   *  baseline. */
  unitsNeeded: number | null;
  /** False only when the proposed unit profit itself is zero or negative —
   *  no volume of additional sales recovers a per-unit loss under the
   *  current cost/fee assumptions. */
  recoverable: boolean;
}

export function computeDiscountOffset(
  current: RowPriceEconomics,
  proposed: RowPriceEconomics
): DiscountOffsetResult {
  if (current.unitProfitCents == null || proposed.unitProfitCents == null) {
    return { giveUpPerUnitCents: null, unitsNeeded: null, recoverable: false };
  }

  const giveUpPerUnitCents = current.unitProfitCents - proposed.unitProfitCents;

  if (proposed.unitProfitCents <= 0) {
    return { giveUpPerUnitCents, unitsNeeded: null, recoverable: false };
  }

  if (current.thirtyDayProfitCents == null) {
    // A real discount that COULD be recovered in principle, but the current
    // velocity is unknown so there's no baseline to size the recovery against.
    return { giveUpPerUnitCents, unitsNeeded: null, recoverable: true };
  }

  const unitsNeeded = Math.max(
    0,
    Math.ceil(current.thirtyDayProfitCents / proposed.unitProfitCents)
  );
  return { giveUpPerUnitCents, unitsNeeded, recoverable: true };
}

// ─────────────────────────────────────────────────────────
// Multi-SKU aggregation — live totals across a scenario map. Financial and
// 30-day coverage are tracked as two independent dimensions per the design
// doc's separate acceptance rules; a row can be financially valued while its
// velocity is unknown (or vice versa is not possible, since 30-day profit
// needs unit profit too).
// ─────────────────────────────────────────────────────────

export interface ScenarioFinancialAggregate {
  valuedUnits: number;
  valuedRowCount: number;
  excludedUnits: number;
  excludedRowCount: number;
  remainingInventoryRevenueCents: number;
  remainingInventoryProfitCents: number;
  sellThroughProfitDeltaCents: number;
}

export interface ScenarioThirtyDayAggregate {
  valuedUnits: number;
  valuedRowCount: number;
  excludedUnits: number;
  excludedRowCount: number;
  /** Subset of excludedRowCount specifically due to unknown (not merely
   *  zero) velocity — the UI's "unknown velocity" warning count. */
  unknownVelocityRowCount: number;
  profitCents: number;
}

export interface ScenarioAggregate {
  financial: ScenarioFinancialAggregate;
  thirtyDay: ScenarioThirtyDayAggregate;
}

/** Rolls up a set of per-row scenarios into live totals. Rows with
 *  selectedUnits <= 0 are skipped entirely — they contribute no dollars and
 *  no coverage, in either direction, at any price (the zero-stock
 *  exclusion rule). Financial validity is keyed on the PROPOSED scenario's
 *  profit completeness (a superset of revenue's requirements); 30-day
 *  validity additionally requires a known velocity. */
export function aggregateScenarioResults(rows: RowScenarioResult[]): ScenarioAggregate {
  const financial: ScenarioFinancialAggregate = {
    valuedUnits: 0,
    valuedRowCount: 0,
    excludedUnits: 0,
    excludedRowCount: 0,
    remainingInventoryRevenueCents: 0,
    remainingInventoryProfitCents: 0,
    sellThroughProfitDeltaCents: 0,
  };
  const thirtyDay: ScenarioThirtyDayAggregate = {
    valuedUnits: 0,
    valuedRowCount: 0,
    excludedUnits: 0,
    excludedRowCount: 0,
    unknownVelocityRowCount: 0,
    profitCents: 0,
  };

  for (const row of rows) {
    if (row.selectedUnits <= 0) continue;

    if (row.proposed.remainingInventoryProfitCents != null) {
      financial.valuedUnits += row.selectedUnits;
      financial.valuedRowCount += 1;
      financial.remainingInventoryRevenueCents += row.proposed.remainingInventoryRevenueCents ?? 0;
      financial.remainingInventoryProfitCents += row.proposed.remainingInventoryProfitCents;
      financial.sellThroughProfitDeltaCents += row.sellThroughProfitDeltaCents ?? 0;
    } else {
      financial.excludedUnits += row.selectedUnits;
      financial.excludedRowCount += 1;
    }

    if (row.proposed.thirtyDayProfitCents != null) {
      thirtyDay.valuedUnits += row.selectedUnits;
      thirtyDay.valuedRowCount += 1;
      thirtyDay.profitCents += row.proposed.thirtyDayProfitCents;
    } else {
      thirtyDay.excludedUnits += row.selectedUnits;
      thirtyDay.excludedRowCount += 1;
      if (row.proposed.thirtyDayUnits == null) thirtyDay.unknownVelocityRowCount += 1;
    }
  }

  return { financial, thirtyDay };
}
